import type { ComponentType } from 'react'

/**
 * A country plugin bundles the country-specific UI entry points that core
 * resolves at runtime. Plugins live under `src/countries/<slug>/` (outside
 * core) and register themselves via `registerCountryPlugin()`.
 *
 * Core never imports a plugin directly — it only reads this registry — so a
 * country can be added or removed without touching core. Plugins depend on
 * core (stores, http, theme), never the other way around.
 */
export interface CountryPlugin {
    /** Build/URL slug, matches __COUNTRY__ (e.g. 'oman'). */
    slug: string
    /** Alpha-3 country code (e.g. 'OMN'). */
    code: string
    /** Checkout page entry (shipping / create-account step). */
    CheckoutModule: ComponentType
    /** Payment page entry. */
    PaymentsModule: ComponentType
    /** Optional order-review page (Purchase step). Not every country has one. */
    ReviewModule?: ComponentType
}

const registry = new Map<string, CountryPlugin>()

export function registerCountryPlugin(plugin: CountryPlugin): void {
    registry.set(plugin.slug.toLowerCase(), plugin)
    registry.set(plugin.code.toUpperCase(), plugin)
}

/** Look up a registered plugin by slug ('oman') or alpha-3 code ('OMN'). */
export function getCountryPlugin(key: string): CountryPlugin | undefined {
    return registry.get(key.toLowerCase()) ?? registry.get(key.toUpperCase())
}

/** The plugin for the country this build targets (VITE_COUNTRY / __COUNTRY__). */
export function getActiveCountryPlugin(): CountryPlugin | undefined {
    return getCountryPlugin(__COUNTRY__)
}
